import { create } from "zustand";
import axios from "axios";
import i18n from "../i18n";

const API_URL = import.meta.env.VITE_API_BASE_URL;

const normalizeLanguage = (language) => (language === "en" ? "en" : "sr");

// Sirina sidebar-a se postavlja inline (framer-motion), pa mora iz JS-a.
// Granice moraju da prate @media blokove u src/css/sidebar.css i src/css/bigScreen.css.
// Visina je uslov jer sidebar raste po vertikali — ne uvecavamo ga ako ne moze da stane.
// Prati @media granice u src/css/bigScreen.css (samo sirina, bez uslova visine).
// Za inline vrednosti koje CSS ne moze da nadjaca (framer-motion animate).
const getBigScreenTier = () => {
    const width = window.innerWidth;

    if (width >= 3500) return 2;
    if (width >= 2700) return 1;
    return 0;
};

const getSidebarSizeTier = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    if (width >= 3500 && height >= 900) return 3;
    if (width >= 2700 && height >= 900) return 2;
    if (width >= 995 && height >= 765) return 1;
    return 0;
};

const useLayoutStore = create((set) => {
    // provera da li je dark tema već sačuvana u localStorage
    const savedTheme = localStorage.getItem("isDarkTheme");
    const initialDarkTheme = savedTheme ? JSON.parse(savedTheme) : false;

    const savedLanguage = localStorage.getItem("appLanguage");
    const initialLanguage = savedLanguage === "en" || savedLanguage === "sr" ? savedLanguage : "sr";

    return {
        // employees
        employeesCardsInRow: 1,

        calculateEmployeesCardsInRow: () => {
            const width = window.innerWidth;
            let cards = 1;

            if (width < 685) cards = 1;
            else if (width < 969) cards = 2;
            else if (width < 1325) cards = 3;
            else if (width < 1600) cards = 4;
            else if (width < 1874) cards = 5;
            else if (width < 2151) cards = 6;
            else if (width < 2428) cards = 7;
            else cards = 8;

            set({ employeesCardsInRow: cards });
        },

        // devices
        devicesCardsInRow: 1,

        calculateDevicesCardsInRow: () => {
            const width = window.innerWidth;
            let cards = 1;

            if (width < 1138) cards = 1;
            else if (width < 1608) cards = 2;
            else if (width < 2072) cards = 3;
            else cards = 4;

            set({ devicesCardsInRow: cards });
        },

        smallMediumScreen: window.innerWidth < 795,
        smallScreen: window.innerWidth < 684,
        sidebarSizeTier: getSidebarSizeTier(),
        bigScreenTier: getBigScreenTier(),

        // dropdown max height
        dropdownMaxHeight: 194,
        activeDropdownElement: null,

        setActiveDropdownElement: (element) => {
            set({ activeDropdownElement: element });
        },

        calculateDropdownMaxHeight: () => {
            const state = useLayoutStore.getState();
            if (!state.activeDropdownElement) {
                set({ dropdownMaxHeight: 194 });
                return;
            }

            const dropdownRect = state.activeDropdownElement.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - dropdownRect.bottom;
            const paddingFromBottom = 9; // Par px od dna ekrana

            // MaxHeight je ili CSS max-height (194px) ili prostor do dna minus padding, šta je manje
            const calculatedMaxHeight = Math.min(194, Math.max(100, spaceBelow - paddingFromBottom));
            set({ dropdownMaxHeight: calculatedMaxHeight });
        },

        // hover/pointer, ne maxTouchPoints — PC sa touch monitorom i mišem i dalje ima hover
        isTouchDevice: window.matchMedia("(hover: none), (pointer: coarse)").matches,

        // 🔥 učitavanje početne teme iz localStorage-a
        isDarkTheme: initialDarkTheme,

        // 🔥 toggle koji i setuje u localStorage
        toggleTheme: () =>
            set((state) => {
                const newTheme = !state.isDarkTheme;
                localStorage.setItem("isDarkTheme", JSON.stringify(newTheme));
                return { isDarkTheme: newTheme };
            }),

        language: initialLanguage,
        languageToast: null,

        setLanguageToast: (toastData) => {
            set({ languageToast: toastData });
        },

        // Samo lokalno (login sync /me) — bez API poziva
        applyLanguage: (language) => {
            const nextLanguage = normalizeLanguage(language);
            localStorage.setItem("appLanguage", nextLanguage);
            i18n.changeLanguage(nextLanguage);
            document.documentElement.lang = nextLanguage;
            set({ language: nextLanguage });
        },

        // Promena jezika u UI — lokalno + upis u bazu (ako je sesija aktivna)
        setLanguage: (language) => {
            const nextLanguage = normalizeLanguage(language);
            useLayoutStore.getState().applyLanguage(nextLanguage);

            axios
                .post(
                    `${API_URL}/api/app/user/language`,
                    { language: nextLanguage },
                    { withCredentials: true }
                )
                .then(() => {
                    set({
                        languageToast: {
                            title: i18n.t("languageChangedTitle", { ns: "profile" }),
                            message: i18n.t("languageChangedMessage", { ns: "profile" }),
                        },
                    });
                })
                .catch(() => {
                    // Nije ulogovan ili greška — lokalna preferenca ostaje, bez toasta
                });
        },
    };
});
// inicijalno izračunavanje
useLayoutStore.getState().calculateEmployeesCardsInRow();
useLayoutStore.getState().calculateDevicesCardsInRow()

// listener na resize
window.addEventListener("resize", () => {
    useLayoutStore.getState().calculateEmployeesCardsInRow();
    useLayoutStore.getState().calculateDevicesCardsInRow();
    useLayoutStore.setState({ smallMediumScreen: window.innerWidth < 795 });
    useLayoutStore.setState({ smallScreen: window.innerWidth < 684 });
    useLayoutStore.setState({ sidebarSizeTier: getSidebarSizeTier() });
    useLayoutStore.setState({ bigScreenTier: getBigScreenTier() });
    useLayoutStore.getState().calculateDropdownMaxHeight();
});

export default useLayoutStore;
